import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Pencil,
  Plus,
  Server as ServerIcon,
  ShieldCheck,
  Trash2,
  XCircle,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ServerCreateModal } from "@/components/servers/ServerCreateModal";
import { ServerEditModal } from "@/components/servers/ServerEditModal";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCurrentUser } from "@/lib/hooks/auth";
import {
  useServerDelete,
  useServersList,
  useServerTest,
} from "@/lib/hooks/servers";
import { ROLE_LEVEL, type UserRole } from "@/lib/schemas/users";
import type { ServerRead, ServerTestResult } from "@/lib/schemas/servers";

export const Route = createFileRoute("/_app/servers")({
  component: ServersPage,
});

function ServersPage(): JSX.Element {
  const { t } = useTranslation();
  const query = useServersList();
  const testMutation = useServerTest();
  const deleteMutation = useServerDelete();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ServerRead | null>(null);
  const [lastTest, setLastTest] = useState<
    Record<string, ServerTestResult | undefined>
  >({});

  // Server provider e' superuser-only (Phase 11.6 — mirror BE _ensure_superuser
  // in servers/router.py). Customer/reseller/admin vedono solo lo stato dei
  // server gia' configurati, NON i CTA write. Provider config impatta tutti
  // i tenant dell'installazione → restricted a platform operator.
  const me = useCurrentUser();
  const canWrite =
    (ROLE_LEVEL[(me.data?.role ?? "peer_read") as UserRole] ?? 0) >=
    ROLE_LEVEL.superuser;

  async function handleTest(server: ServerRead): Promise<void> {
    try {
      const result = await testMutation.mutateAsync(server.id);
      setLastTest((prev) => ({ ...prev, [server.id]: result }));
    } catch {
      // surfaced via mutation.error
    }
  }

  async function handleDelete(server: ServerRead): Promise<void> {
    if (!window.confirm(t("servers.delete.confirm", { name: server.name }))) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(server.id);
    } catch {
      // surfaced via mutation.error
    }
  }

  const items = query.data?.items ?? [];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">
            {t("servers.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("servers.subtitle")}
          </p>
        </div>
        {canWrite && (
          <Button
            type="button"
            onClick={() => setCreateOpen(true)}
            size="sm"
            className="gap-1.5"
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("servers.create.button")}
          </Button>
        )}
      </header>

      <ServerCreateModal open={createOpen} onOpenChange={setCreateOpen} />
      <ServerEditModal
        open={editTarget !== null}
        onOpenChange={(v) => {
          if (!v) setEditTarget(null);
        }}
        server={editTarget}
      />

      {query.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {t("servers.load_error")} — {query.error.message}
        </div>
      )}

      {!query.isLoading && items.length === 0 && (
        <div className="space-y-3 rounded-md border border-dashed border-border py-12 text-center">
          <ServerIcon className="mx-auto size-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t("servers.empty")}</p>
          {canWrite && (
            <Button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="gap-1.5"
            >
              <Plus className="size-4" aria-hidden="true" />
              {t("servers.create.button")}
            </Button>
          )}
        </div>
      )}

      {items.length > 0 && (
        <div className="rounded-md border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("servers.fields.name")}</TableHead>
                <TableHead>{t("servers.fields.provider")}</TableHead>
                <TableHead>{t("servers.fields.endpoint_url")}</TableHead>
                <TableHead>{t("servers.fields.verify_tls_short")}</TableHead>
                <TableHead>{t("servers.fields.status")}</TableHead>
                <TableHead>{t("servers.fields.credentials_ref")}</TableHead>
                <TableHead>{t("servers.fields.test")}</TableHead>
                <TableHead aria-label="actions"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((s) => {
                const test = lastTest[s.id];
                return (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell>
                      <span className="rounded-md border border-border bg-muted px-1.5 py-0.5 font-mono text-xs">
                        {s.provider}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[280px] truncate font-mono text-xs">
                      {s.endpoint_url}
                    </TableCell>
                    <TableCell>
                      {s.verify_tls ? (
                        <span
                          className="inline-flex items-center gap-1 text-xs text-success"
                          title={t("servers.fields.verify_tls_hint")}
                        >
                          <ShieldCheck className="size-3.5" aria-hidden="true" />
                          {t("common.yes")}
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 text-xs text-warning"
                          title={t("servers.fields.verify_tls_hint")}
                        >
                          <AlertTriangle className="size-3.5" aria-hidden="true" />
                          {t("common.no")}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`text-xs ${
                          s.status === "active"
                            ? "text-success"
                            : s.status === "disabled"
                              ? "text-destructive"
                              : "text-warning"
                        }`}
                      >
                        {t(`servers.status.${s.status}`)}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-[10px] text-muted-foreground">
                      {s.credentials_ref ? "✓ OpenBao" : t("servers.fields.no_credentials")}
                    </TableCell>
                    <TableCell>
                      {test ? (
                        <span
                          className={`flex items-center gap-1 text-xs ${
                            test.status === "ok" ? "text-success" : "text-destructive"
                          }`}
                        >
                          {test.status === "ok" ? (
                            <CheckCircle2 className="size-3.5" />
                          ) : (
                            <XCircle className="size-3.5" />
                          )}
                          {test.latency_ms != null
                            ? `${test.latency_ms}ms`
                            : test.error_kind}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {t("servers.fields.not_tested")}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void handleTest(s)}
                          disabled={testMutation.isPending}
                          aria-label={t("servers.test.button")}
                        >
                          {testMutation.isPending &&
                          testMutation.variables === s.id ? (
                            <Loader2 className="size-3.5 animate-spin" />
                          ) : (
                            <Zap className="size-3.5" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditTarget(s)}
                          aria-label={t("servers.edit.button")}
                        >
                          <Pencil className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void handleDelete(s)}
                          disabled={deleteMutation.isPending}
                          aria-label={t("common.delete")}
                        >
                          <Trash2 className="size-3.5 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {testMutation.error && (
        <p className="text-xs text-destructive" role="alert">
          {testMutation.error.message}
        </p>
      )}
      {deleteMutation.error && (
        <p className="text-xs text-destructive" role="alert">
          {deleteMutation.error.message}
        </p>
      )}
    </div>
  );
}
